// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from 'react';
import ConfirmationModal from '../../components/ConfirmationModal';
import CorrectionRow from './CorrectionRow';
import LoadingScreen from '../../components/LoadingScreen';

import { AuthContext } from '../../context/AuthContext';
import { firestore } from '../../context/FirebaseConfig';

import { Link } from 'react-router-dom';
import {
  deleteDoc,
  updateDoc,
  getDoc,
  doc,
  where,
  query,
  orderBy,
  onSnapshot,
  collectionGroup,
} from 'firebase/firestore';
import { subHours } from 'date-fns';

// Importing filters
import TimeframeFilter from '../../components/Filters/TimeFrameFilter';
import SupplierFilter from '../../components/Filters/SupplierFilter';

// Importing icons
import { ChevronLeft } from 'lucide-react';
import { useTranslation } from '../../i18n/i18n';

export default function Corrections() {
  const { currentUser, stockFields, suppliers } = React.useContext(AuthContext);
  const { t } = useTranslation(currentUser?.info?.languageCode);
  const [lastDocuments, setLastDocuments] = React.useState([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = React.useState(true);
  const [confirmDeleteDocId, setConfirmDeleteDocId] = React.useState(null);
  const [editField, setEditField] = React.useState(null);
  const [editFieldContent, setEditFieldContent] = React.useState({ supplier_id: '', field: '' });
  const [linkedDevices, setLinkedDevices] = React.useState([]);

  // Filter states
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [timeframe, setTimeframe] = React.useState({
    startDate: firstDayOfMonth,
    endDate: today,
    label: 'timeframe_this_month',
  });
  const [supplierFilter, setSupplierFilter] = React.useState('');
  const [filteredDocuments, setFilteredDocuments] = React.useState({});

  // Pagination states
  const [visibleDocuments, setVisibleDocuments] = React.useState({});
  const [currentPage, setCurrentPage] = React.useState(1);
  const documentsPerPage = 20;
  const documentListRef = React.useRef(null);

  // Get unique suppliers from documents
  const uniqueSuppliers = React.useMemo(() => {
    const supplierIds = Object.values(lastDocuments).map((doc) => doc.supplier_id);
    const uniqueList = [...new Set(supplierIds)].filter(Boolean);

    // Sort suppliers alphabetically by their display names
    return uniqueList.sort((a, b) => {
      const nameA = suppliers?.[a]?.display_name || a || '';
      const nameB = suppliers?.[b]?.display_name || b || '';
      return nameA.localeCompare(nameB, undefined, { numeric: true });
    });
  }, [lastDocuments, suppliers]);

  React.useEffect(() => {
    subscribeToDocuments();
  }, []);

  // Apply filters when documents or filter values change
  React.useEffect(() => {
    const filtered = {};
    Object.entries(lastDocuments).forEach(([id, doc]) => {
      // Safely extract date from document
      let docDate = doc.start_timestamp?.toDate ? doc.start_timestamp.toDate() : null;

      // Skip documents without a valid timestamp
      if (!docDate) return;

      // Check if document date is within the selected timeframe
      // Make sure we're comparing just the date portion for equality checks
      // to avoid issues with time portions (for same-day comparisons)
      const isInTimeframe = docDate >= timeframe.startDate && docDate <= timeframe.endDate;

      // Check if document matches supplier filter
      const matchesSupplier = !supplierFilter || doc.supplier_id === supplierFilter;

      // Add to filtered documents if it matches both conditions
      if (isInTimeframe && matchesSupplier) {
        filtered[id] = doc;
      }
    });
    setFilteredDocuments(filtered);
  }, [lastDocuments, timeframe, supplierFilter]);

  // Update visible documents when filtered documents or page changes
  React.useEffect(() => {
    const documentIds = Object.keys(filteredDocuments);
    const startIndex = 0;
    const endIndex = Math.min(currentPage * documentsPerPage, documentIds.length);

    const visibleDocs = {};
    for (let i = startIndex; i < endIndex; i++) {
      const id = documentIds[i];
      visibleDocs[id] = filteredDocuments[id];
    }

    setVisibleDocuments(visibleDocs);
  }, [filteredDocuments, currentPage]);

  // Handle scroll event to implement infinite scrolling
  React.useEffect(() => {
    const handleScroll = () => {
      if (!documentListRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = documentListRef.current;
      const scrollPosition = scrollTop + clientHeight;

      // If scrolled to bottom (with a small threshold)
      if (scrollHeight - scrollPosition < 50) {
        const totalPages = Math.ceil(Object.keys(filteredDocuments).length / documentsPerPage);

        if (currentPage < totalPages) {
          setCurrentPage((prevPage) => prevPage + 1);
        }
      }
    };

    const listElement = documentListRef.current;
    if (listElement) {
      listElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (listElement) {
        listElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [filteredDocuments, currentPage]);

  async function subscribeToDocuments() {
    // Get current device data to get linked devices
    const currentDeviceDoc = await getDoc(
      doc(firestore, 'clients', currentUser.company.company, 'devices', currentUser.device_id),
    );
    const currentDeviceData = currentDeviceDoc.data();
    setLinkedDevices(currentDeviceData?.last_plant_feed_selection?.linked_devices ?? []);

    const deviceSubscriptions = [
      currentUser.device_id,
      ...(currentDeviceData?.last_plant_feed_selection?.linked_devices ?? []),
    ];

    // Query for the last 30 days instead of 24 hours to have more data for filtering
    const q = query(
      collectionGroup(firestore, 'bale_feed_timestamps'),
      where('client_id', '==', currentUser.company.company),
      where('device_id', 'in', deviceSubscriptions),
      where('start_timestamp', '>=', subHours(new Date(), 30 * 24)), // 30 days
      orderBy('start_timestamp', 'desc'),
    );

    onSnapshot(q, (snapshot) => {
      const documentsNew = {};
      snapshot.forEach((docSnapshot) => {
        documentsNew[docSnapshot.id] = docSnapshot.data();
      });
      setLastDocuments(documentsNew);
      setIsLoadingDocuments(false);
    });
  }

  function handleDeleteDocument() {
    const clientId = lastDocuments[confirmDeleteDocId]?.client_id ?? currentUser.company.company;
    const deviceId = lastDocuments[confirmDeleteDocId]?.device_id ?? currentUser.device_id;

    const q = doc(firestore, 'clients', clientId, 'devices', deviceId, 'bale_feed_timestamps', confirmDeleteDocId);
    deleteDoc(q);
    setConfirmDeleteDocId(null);
  }

  function handleEditField(id) {
    setEditFieldContent({
      supplier_id: lastDocuments[id].supplier_id,
      field: lastDocuments[id].field,
    });
    setEditField(id);
  }

  function handleAcceptEdit() {
    const clientId = lastDocuments[editField]?.client_id ?? currentUser.company.company;
    const deviceId = lastDocuments[editField]?.device_id ?? currentUser.device_id;

    const q = doc(firestore, 'clients', clientId, 'devices', deviceId, 'bale_feed_timestamps', editField);
    updateDoc(q, editFieldContent);
    setEditField(null);
  }

  return (
    <div className='h-screen box-border flex flex-col bg-white text-[#16192c] dark:bg-[#0f172a] dark:text-[#fafafa]'>
      <div className='relative z-10 flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0] dark:border-[rgba(63,82,98,0.2)] dark:bg-[linear-gradient(180deg,rgba(39,115,183,0.24)_25%,rgba(12,18,29,0.24)_100%)]'>
        <Link
          className='inline-flex items-center gap-2 rounded-lg p-1 pr-3 w-fit transition-colors duration-200 text-[#a2a3ab] hover:bg-black/5 active:bg-black/10 dark:text-[#cad5e2] dark:hover:bg-white/15 dark:active:bg-white/25'
          to='/feed-timestamping'
        >
          <ChevronLeft className='size-6 shrink-0' />
          <h1 className='text-xl font-semibold text-[#16192c] dark:text-[#fafafa]'>{t('corrections_page_title')}</h1>
        </Link>
        <div className='flex items-center gap-4 max-md:flex-col max-md:items-end max-md:gap-2'>
          <TimeframeFilter timeframe={timeframe} setTimeframe={setTimeframe} />
          {uniqueSuppliers.length > 0 && (
            <SupplierFilter occurringSuppliers={uniqueSuppliers} setSupplierFilter={setSupplierFilter} />
          )}
        </div>
      </div>
      <div className='documents-list flex-1 overflow-y-auto p-4 flex flex-col gap-5' ref={documentListRef}>
        {isLoadingDocuments ? (
          <LoadingScreen loading='Loading...' fullscreen={false} />
        ) : Object.keys(visibleDocuments).length > 0 ? (
          Object.keys(visibleDocuments).map((id) => {
            let supplierName;
            if (suppliers?.[visibleDocuments[id].supplier_id]?.supplier_code) {
              supplierName = `${suppliers?.[visibleDocuments[id].supplier_id]?.supplier_code} ${suppliers?.[visibleDocuments[id].supplier_id]?.display_name}`;
            } else {
              supplierName =
                suppliers?.[visibleDocuments[id].supplier_id]?.display_name ?? visibleDocuments[id].supplier_id;
            }
            const fieldName = stockFields?.[visibleDocuments[id].field]?.display_name ?? visibleDocuments[id].field;
            return (
              <CorrectionRow
                key={id}
                id={id}
                document={visibleDocuments[id]}
                supplierName={supplierName}
                fieldName={fieldName}
                isEditing={editField === id}
                editFieldContent={editFieldContent}
                setEditFieldContent={setEditFieldContent}
                suppliers={suppliers}
                stockFields={stockFields}
                linkedDevices={linkedDevices}
                currentUser={currentUser}
                onEdit={handleEditField}
                onAcceptEdit={handleAcceptEdit}
                onCancelEdit={() => setEditField(null)}
                onDelete={setConfirmDeleteDocId}
              />
            );
          })
        ) : (
          <div className='flex justify-center items-center h-[12.5rem] rounded-lg bg-[#E7EEF7] dark:bg-[#22293B] border border-[#E8EFFB] dark:border-[#ffffff14]'>
            <p className='opacity-70 text-[#16192c] dark:text-[#fafafa]'>{t('corrections_no_documents')}</p>
          </div>
        )}
        {Object.keys(filteredDocuments).length > Object.keys(visibleDocuments).length && (
          <div className='flex justify-center items-center p-4 '>
            <p className='opacity-70 italic text-[#16192c] dark:text-[#fafafa]'>{t('corrections_loading_more')}</p>
          </div>
        )}
      </div>

      {confirmDeleteDocId && (
        <ConfirmationModal
          title={t('production_corrections_delete_timestamp')}
          subTitle={t('production_corrections_delete_timestamp_message')}
          confirmText={t('common_delete')}
          handleCancel={() => setConfirmDeleteDocId(null)}
          handleConfirm={() => handleDeleteDocument()}
        />
      )}
    </div>
  );
}
